import isArray from 'lodash/isArray';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';


/** Whether `key` is a real field of the yup `schema` (so a matching server error
 * maps to a field rather than the non-field bucket). */
function hasKey(key, schema) {
  if (schema.type === 'object') {
    return key in schema.fields;
  }

  if (schema.type === 'array') {
    if (schema.innerType.type === 'object') {
      return hasKey(key, schema.innerType);
    }
  }

  return false;
}


/**
 * Walk a DRF 400 error payload against the form's yup `schema` and dispatch each
 * entry to `setError(fieldPath, { type, message })` (RHF's setError) or to
 * `nonFieldError({ key, detail })` when the key is not a schema field.
 *
 * Field values arrive as a plain string, an array of strings (DRF's default —
 * e.g. `{ price: ['Too high'] }`), or nested object/array shapes for nested
 * serializers/formsets. For an array of strings the FIRST string is used as the
 * field message (RHF shows one message per field); object items recurse by index.
 * A guard aborts pathological/cyclic payloads after 10k nodes.
 */
function parseServerErrors(schema, data, setError, nonFieldError = () => {}) {
  let counter = 0;

  const walk = (s, d, prefix = '') => {
    if (counter >= 10000) {
      throw new Error('Unable to parse object.');
    }
    counter += 1;

    for (const [key, value] of Object.entries(d)) {
      const errorKey = prefix ? `${prefix}.${key}` : key;

      if (isString(value)) {
        if (hasKey(key, s)) {
          setError(errorKey, { type: 'invalid', message: value });
        }
        else {
          nonFieldError({ key: errorKey, detail: value });
          break;
        }
      }
      else if (isArray(value)) {
        if (hasKey(key, s)) {
          const firstString = value.find(isString);
          if (firstString !== undefined) {
            setError(errorKey, { type: 'invalid', message: firstString });
          }
          value.forEach((item, index) => {
            if (isObject(item)) {
              walk(s.fields[key], item, `${errorKey}[${index}]`);
            }
          });
        }
        else {
          nonFieldError({ key: errorKey, detail: value });
          break;
        }
      }
      else if (isObject(value)) {
        walk(s.fields[key], value, errorKey);
      }
    }
  };

  walk(schema, data);
}


export { hasKey, parseServerErrors };
export default parseServerErrors;
